/** A position on the tile grid. One tile is 1 m² of the real office. */
export interface TilePos {
  readonly x: number;
  readonly y: number;
}

/** The four movement directions. Diagonal movement is disabled by design (see the spec, §6.4). */
export const DIRECTIONS = ['up', 'down', 'left', 'right'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const DIRECTION_DELTAS: Readonly<Record<Direction, TilePos>> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

/** Returns the neighbouring tile in the given direction (may be outside the grid). */
export function step(from: TilePos, direction: Direction): TilePos {
  const delta = DIRECTION_DELTAS[direction];
  return { x: from.x + delta.x, y: from.y + delta.y };
}

/** Direction from `from` to an orthogonally adjacent tile `to`, or `null` if they are not adjacent. */
export function directionBetween(from: TilePos, to: TilePos): Direction | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === -1) return 'up';
  if (dx === 0 && dy === 1) return 'down';
  if (dx === -1 && dy === 0) return 'left';
  if (dx === 1 && dy === 0) return 'right';
  return null;
}

/** Manhattan distance: the A* heuristic for 4-directional movement. */
export function manhattan(a: TilePos, b: TilePos): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

/** Chebyshev distance: used for interaction range (a player diagonally next to an object is in range 1). */
export function chebyshev(a: TilePos, b: TilePos): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** A rectangle of tiles: top-left corner plus size (both at least 1). */
export interface TileRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Chebyshev distance from a tile to the nearest tile of a rectangle (0 when inside it). This is
 * how far a player is from a multi-tile object such as a 2×1 desk (spec §6.6).
 */
export function chebyshevToRect(pos: TilePos, rect: TileRect): number {
  const dx = Math.max(rect.x - pos.x, 0, pos.x - (rect.x + rect.w - 1));
  const dy = Math.max(rect.y - pos.y, 0, pos.y - (rect.y + rect.h - 1));
  return Math.max(dx, dy);
}

export function samePos(a: TilePos, b: TilePos): boolean {
  return a.x === b.x && a.y === b.y;
}
